import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, ImagePlus, Loader2, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { inferAllergens } from '@/lib/allergens'
import type { WeeklyDish, WeeklyDishType } from '@/lib/weeklyDishes'

interface WeeklyDishFormProps {
  isOpen: boolean
  initialDish?: WeeklyDish | null
  onClose: () => void
  onSave: (
    data: Omit<WeeklyDish, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }
  ) => void
}

export function WeeklyDishForm({
  isOpen,
  initialDish,
  onClose,
  onSave,
}: WeeklyDishFormProps) {
  const { t, i18n } = useTranslation('admin')

  const [type, setType] = useState<WeeklyDishType>('primo')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState('')
  const [allergens, setAllergens] = useState('')
  const [image, setImage] = useState('')
  const [previewName, setPreviewName] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle')
  const [formError, setFormError] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (initialDish) {
      setType(initialDish.type)
      setTitle(initialDish.title)
      setDescription(initialDish.description)
      setPrice(initialDish.price ?? '')
      setAllergens(initialDish.allergens ?? '')
      setImage(initialDish.image ?? '')
      setPreviewName('')
    } else {
      setType('primo')
      setTitle('')
      setDescription('')
      setPrice('')
      setAllergens('')
      setImage('')
      setPreviewName('')
    }
    setStatus('idle')
    setFormError(false)
  }, [initialDish, isOpen])

  useEffect(() => {
    if (!allergens.trim()) {
      const lang = (i18n.language as 'it' | 'en') ?? 'it'
      setAllergens(inferAllergens(`${title} ${description}`, lang))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  }, [isOpen])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setFormError(true)
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      setImage(reader.result as string)
      setPreviewName(file.name)
      setFormError(false)
    }
    reader.readAsDataURL(file)
  }

  const handleRemovePhoto = () => {
    setImage('')
    setPreviewName('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const resetForm = () => {
    setType('primo')
    setTitle('')
    setDescription('')
    setPrice('')
    setAllergens('')
    setImage('')
    setPreviewName('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!title.trim() || !image) {
      setFormError(true)
      return
    }

    setFormError(false)
    setStatus('submitting')

    setTimeout(() => {
      onSave({
        id: initialDish?.id,
        type,
        title: title.trim(),
        description: description.trim(),
        price: price.trim() || undefined,
        allergens: allergens.trim() || undefined,
        image,
      })
      setStatus('success')
      setTimeout(() => {
        resetForm()
        onClose()
      }, 800)
    }, 400)
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="weekly-dish-form-title"
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:p-10">
        <div className="mb-6 flex items-center justify-between">
          <h2
            id="weekly-dish-form-title"
            className="font-display text-2xl font-bold text-ink"
          >
            {initialDish
              ? t('zone.weeklyDishes.editDish')
              : t('zone.weeklyDishes.addDish')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('aria.close')}
            className="rounded-full p-2 text-ink/70 transition-colors hover:bg-cord hover:text-ink"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Type */}
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-ink">
              {t('zone.weeklyDishes.type')}
            </span>
            <select
              value={type}
              onChange={(e) => {
                setType(e.target.value as WeeklyDishType)
                setFormError(false)
              }}
              className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20"
            >
              <option value="primo">{t('zone.weeklyDishes.typePrimo')}</option>
              <option value="secondo">{t('zone.weeklyDishes.typeSecondo')}</option>
            </select>
          </label>

          {/* Title */}
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-ink">
              {t('zone.weeklyDishes.titleLabel')}
            </span>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value)
                setFormError(false)
              }}
              placeholder={t('zone.weeklyDishes.titlePlaceholder')}
              required
              className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20"
            />
          </label>

          {/* Description */}
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-ink">
              {t('zone.weeklyDishes.descriptionLabel')}
            </span>
            <textarea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value)
                setFormError(false)
              }}
              placeholder={t('zone.weeklyDishes.descriptionPlaceholder')}
              rows={4}
              className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20"
            />
          </label>

          {/* Price */}
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-ink">
              {t('zone.weeklyDishes.priceLabel')}
            </span>
            <input
              type="text"
              value={price}
              onChange={(e) => {
                setPrice(e.target.value)
                setFormError(false)
              }}
              placeholder={t('zone.weeklyDishes.pricePlaceholder')}
              className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20"
            />
          </label>

          {/* Allergens */}
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-ink">
              {t('zone.weeklyDishes.allergensLabel')}
            </span>
            <input
              type="text"
              value={allergens}
              onChange={(e) => {
                setAllergens(e.target.value)
                setFormError(false)
              }}
              placeholder={t('zone.weeklyDishes.allergensPlaceholder')}
              className="block w-full rounded-xl border border-cord bg-cream px-4 py-3 text-ink transition-colors focus:border-maia focus:bg-white focus:outline-none focus:ring-2 focus:ring-maia/20"
            />
          </label>

          {/* Photo upload */}
          <div className="block">
            <span className="mb-2 block text-sm font-semibold text-ink">
              {t('zone.weeklyDishes.photoLabel')}
            </span>
            <div
              className={cn(
                'relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition-colors',
                image
                  ? 'border-maia bg-cream'
                  : 'border-cord bg-cream hover:border-maia hover:bg-cream/80'
              )}
            >
              {image ? (
                <div className="w-full space-y-4">
                  <img
                    src={image}
                    alt={t('zone.weeklyDishes.photoPreviewAlt')}
                    className="mx-auto h-64 rounded-lg object-cover aspect-[3/4]"
                  />
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-sm font-medium text-ink">
                      {previewName || t('zone.weeklyDishes.photoPreviewAlt')}
                    </span>
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="rounded-full bg-pizza px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-pizza-dark"
                    >
                      {t('zone.weeklyDishes.removePhoto')}
                    </button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer space-y-2">
                  <ImagePlus
                    className="mx-auto h-10 w-10 text-maia/60"
                    aria-hidden="true"
                  />
                  <span className="block text-sm font-semibold text-ink">
                    {t('zone.weeklyDishes.photoHint')}
                  </span>
                  <span className="block text-xs text-muted">
                    {t('zone.weeklyDishes.photoFormats')}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="sr-only"
                  />
                </label>
              )}
            </div>
          </div>

          {formError && (
            <p className="text-sm font-medium text-pizza">
              {t('zone.weeklyDishes.errorFill')}
            </p>
          )}

          {status === 'success' && (
            <div className="flex items-center gap-2 rounded-xl bg-maia/10 px-4 py-3 text-sm font-semibold text-maia-dark">
              <Check className="h-4 w-4" aria-hidden="true" />
              {t('zone.weeklyDishes.successMessage')}
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full rounded-full bg-maia px-8 py-3 text-sm font-bold text-white shadow-lg transition-colors hover:bg-maia-dark disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
            >
              {status === 'submitting' ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('zone.weeklyDishes.submitting')}
                </span>
              ) : (
                t('zone.weeklyDishes.submit')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
